/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Y from 'yjs';
import { ORIGIN_LOCAL } from './constants';

export function yToExcalidrawElements(yArr: Y.Array<Y.Map<any>>) {
	return yArr.toArray().map(yMapToPlain);
}

function toYMap(el: any) {
	if (!el?.id || !el?.type) {
		console.warn('Invalid element in toYMap:', el);
		//return null;
	}

	const m = new Y.Map<any>();
	for (const [k, v] of Object.entries(el)) {
		// if (v === undefined) {
		// 	console.warn('Undefined value in toYMap for key and element:', k, el);
		// 	//continue;
		// }
		if (k === 'points' && Array.isArray(v)) {
			// store points as a Y.Array so growth can be incremental if you want later
			const yPts = new Y.Array<any>();
			for (const p of v) yPts.push([p]); // keep as array-of-arrays for now
			m.set('points', yPts);
		} else {
			m.set(k, v);
		}
	}
	return m;
}

export function yMapToPlain(m: Y.Map<any>) {
	if (!m) {
		console.warn('Invalid Y.Map in yMapToPlain:', m);
	}
	const o: any = {};
	m.forEach((v, k) => {
		if (k === 'points' && v instanceof Y.Array) {
			o.points = v.toArray().map((p: any) => (Array.isArray(p) ? p : [p]));
		} else {
			o[k] = v;
		}
	});
	return o;
}

export function purgeByIds(yArr: Y.Array<any>, ids: string[]) {
	if (ids.length === 0) return;
	console.log('Purging elements from Yjs by IDs:', ids);
	const doc = yArr.doc!;
	const idSet = new Set(ids);
	const curr = yArr.toArray() as any[];
	Y.transact(
		doc,
		() => {
			for (let i = curr.length - 1; i >= 0; i--) {
				if (idSet.has(curr[i]?.id)) {
					yArr.delete(i, 1);
					curr.splice(i, 1);
				}
			}
		},
		ORIGIN_LOCAL
	);
}

export function syncElementsWithYArray(yArr: Y.Array<Y.Map<any>>, next: readonly any[]) {
	const doc = yArr.doc!;

	const nextLive = next.filter((e) => !!e && e.id && typeof e.version === 'number' && !Number.isNaN(e.version));

	for (const e of nextLive) {
		if (!e.id || typeof e.version !== 'number' || Number.isNaN(e.version)) {
			console.warn('Incomplete element before syncing to Yjs:', e);
		}
	}

	// Build id → index for current Y state
	const curr = yArr.toArray(); // Y.Maps
	const currIndexById = new Map<string, number>();
	for (let i = 0; i < curr.length; i++) {
		const id = curr[i].get('id');
		if (id) currIndexById.set(id, i);
	}

	//console.log("initiating syncElementsWithYArray: nextLive.length='", nextLive.length, "', curr.length='", curr.length, "'");

	Y.transact(
		doc,
		() => {
			// 1) Update or insert each desired element (don’t delete unknowns!)
			for (let i = 0; i < nextLive.length; i++) {
				const desired = nextLive[i];

				if (!desired.id || !Number.isFinite(desired.version) || typeof desired.type !== 'string') {
					console.warn('Refusing to sync invalid element → skipping', desired);
					continue; // don’t insert/update this one
				}

				//console.log(" syncing element index='", i, "', id='", desired.id, "', version='", desired.version, "'");

				const id = desired.id;
				const currIdx = currIndexById.get(id);

				if (currIdx == null) {
					//console.log('Element not found in Yjs, will INSERT:', id);
					// INSERT at correct position
					const yMap = toYMap(desired);
					yArr.insert(i, [yMap]);
					curr.splice(i, 0, yMap);
					// shift subsequent indices
					for (let k = i; k < curr.length; k++) currIndexById.set(curr[k].get('id'), k);
					//console.log('Inserted new element into Yjs at index', i, 'with id', id, { desired, yMap });
					continue;
				}

				// MOVE if needed
				// if (currIdx !== i) {
				// 	console.log('Element found in Yjs but at different index, will MOVE:', id, 'from', currIdx, 'to', i);
				// 	const [moved] = curr.splice(currIdx, 1);
				// 	console.log('Removing from index', currIdx, 'and inserting at', i, 'for element id', id);
				// 	yArr.delete(currIdx, 1);
				// 	console.log('Deleted from Yjs at index', currIdx, 'for element id', id);
				// 	yArr.insert(i, [moved]);
				// 	console.log('Inserted into Yjs at index', i, 'for element id', id);
				// 	curr.splice(i, 0, moved);
				// 	console.log('Moved element in local curr array at index', i, 'for element id', id);
				// 	console.log('Updating currIndexById map after move for element id', id, { currLength: curr.length });
				// 	for (let k = Math.min(currIdx, i); k < curr.length; k++) {
				// 		console.log(' - setting currIndexById for id', curr[k].get('id'), 'to index', k);
				// 		currIndexById.set(curr[k].get('id'), k);
				// 		console.log('   - done');
				// 	}

				// 	console.log('Moved existing element in Yjs from index', currIdx, 'to', i, 'with id', id);
				// }

				// FIELD-LEVEL UPDATE (mutate Y.Map in place)
				//const yMap = curr[i];

				const targetIndex = currIdx ?? i;
				const yMap = curr[targetIndex];

				//console.log('Updating primitives and shallow props:', id);
				// Primitives & shallow props
				for (const [k, v] of Object.entries(desired)) {
					if (k === 'id') continue; // never update id
					if (k === 'points') continue; // handle below
					if (v === undefined) {
						if (yMap.has(k)) yMap.delete(k); // don't store undefined in Y
						continue;
					}
					const old = yMap.get(k);
					if (old !== v) yMap.set(k, v);
				}

				//console.log('Updating points array if needed:', id);
				// Points: simple replace for now (optional: incremental append)
				if (Array.isArray(desired.points)) {
					const yPts = yMap.get('points');
					if (!(yPts instanceof Y.Array)) {
						const n = new Y.Array<any>();
						for (const p of desired.points) n.push([...p]);
						yMap.set('points', n);
						//console.log('Initialized points array in Yjs for element id', id);
					} else {
						// cheap compare by length + last point
						const currPts = yPts.toArray();
						const needReplace =
							currPts.length !== desired.points.length ||
							JSON.stringify(currPts[currPts.length - 1]) !==
								JSON.stringify(desired.points[desired.points.length - 1]);

						if (needReplace) {
							// replace whole points array atomically (fine to start)
							yPts.delete(0, yPts.length);
							for (const p of desired.points) yPts.push([...p]);
							//console.log('Replaced points array in Yjs for element id', id);
						}
					}
				}

				//const plain = yMapToPlain(yMap);
				//console.log('Updated element in Yjs at index', i, 'with id', id, { desired, plain });
			}

			// 2) Deletions: only update delete-flag elements that are explicitly flagged deleted in NEXT
			const nextIdSet = new Set(nextLive.map((e) => e.id));
			for (let i = curr.length - 1; i >= 0; i--) {
				const id = curr[i].get('id');
				if (!nextIdSet.has(id)) {
					const desired = (next as any[]).find((e) => e.id === id);
					if (desired?.isDeleted) {
						const yMap = curr[i];
						if (yMap.get('isDeleted') !== true) {
							yMap.set('isDeleted', true);
							//console.log('Marked element as deleted in Yjs:', id);
						}

						//console.log('Deleted element from Yjs at index', i, 'with id', id);
					}
				}
			}
		},
		ORIGIN_LOCAL
	);

	//console.log('Completed syncElementsWithYArray, final Yjs length=', yArr.length);

	const afterTransaction = yToExcalidrawElements(yArr);
	for (const e of afterTransaction) {
		if (!e.id || typeof e.version !== 'number' || Number.isNaN(e.version)) {
			console.warn('Incomplete element after syncing to Yjs:', e);
		}
	}
}

export function versionHash(list: readonly any[]) {
	// id + version is enough; super cheap
	const elsWithoutIds = list.filter((e: any) => !e?.id);
	if (elsWithoutIds.length > 0) {
		console.warn(`${Date.now()}: versionHash: some elements are missing IDs:`, elsWithoutIds);
	}

	const fast = fnv1a64(list);
	return fast;

	//return list.map((e) => `${e.id}:${e.version}:${e.isDeleted ? 'D' : 'A'}`).join('|');
}

function fnv1a64(list: readonly any[]): string {
	let hashHigh = 0xcbf29ce4 >>> 0,
		hashLow = 0x84222325 >>> 0; // 64-bit split
	for (const e of list) {
		const key = `${e.id}:${e.version}:${e.isDeleted ? 'D' : 'A'}`;
		for (let i = 0; i < key.length; i++) {
			const c = key.charCodeAt(i);
			hashLow ^= c;
			// 64-bit multiply by FNV prime (0x100000001b3)
			const newLow = (hashLow * 0x1b3) >>> 0;
			const newHigh = (hashHigh * 0x1b3 + (hashLow * 0x1b3) / 0x100000000) >>> 0;
			hashLow = newLow;
			hashHigh = newHigh;
		}
	}
	return hashHigh.toString(16).padStart(8, '0') + hashLow.toString(16).padStart(8, '0');
}
